import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import FieldMappingEditor from '@/components/FieldMappingEditor';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface FileUploadResponse {
  fileName: string;
  size: number;
  preview: any[];
  totalRows: number;
  sourceFields: string[];
}

type FileType = 'csv' | 'xlsx' | 'json';

type Step = 'setup' | 'uploaded' | 'mapped' | 'imported';

const TARGET_FIELDS: Array<{ name: string; required: boolean; description?: string }> = [
  { name: 'name', required: true, description: 'Product name' },
  { name: 'sku', required: true, description: 'Product SKU/code' },
  { name: 'price', required: true, description: 'Selling price' },
  { name: 'cost', required: false, description: 'Cost price' },
  { name: 'taxRate', required: false, description: 'Tax rate (%)' },
  { name: 'description', required: false, description: 'Product description' },
  { name: 'category', required: false, description: 'Product category' },
  { name: 'stockQuantity', required: false, description: 'Stock quantity (imports into default branch stock)' },
  { name: 'minStockLevel', required: false, description: 'Minimum stock level' },
  { name: 'barcode', required: false, description: 'Barcode' },
  { name: 'supplier', required: false, description: 'Supplier name' },
  { name: 'isActive', required: false, description: 'Active flag (true/false)' },
];

const normalizeFieldName = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');

const suggestMapping = (sourceFields: string[]): Record<string, string> => {
  const byNormalized = new Map<string, string>();
  for (const f of sourceFields) byNormalized.set(normalizeFieldName(f), f);

  const pick = (candidates: string[]) => {
    for (const c of candidates) {
      const found = byNormalized.get(normalizeFieldName(c));
      if (found) return found;
    }
    return undefined;
  };

  const mapping: Record<string, string> = {};

  const suggestions: Record<string, string[]> = {
    name: ['name', 'product_name', 'item_name', 'title'],
    sku: ['sku', 'code', 'product_code', 'item_code', 'barcode_sku'],
    price: ['price', 'selling_price', 'sale_price', 'unit_price', 'rate'],
    cost: ['cost', 'cost_price', 'buying_price', 'purchase_price'],
    taxRate: ['taxrate', 'tax_rate', 'vat', 'vat_rate'],
    description: ['description', 'desc', 'details'],
    category: ['category', 'group', 'department'],
    stockQuantity: ['stockquantity', 'stock_quantity', 'stock', 'qty', 'quantity', 'on_hand'],
    minStockLevel: ['minstocklevel', 'min_stock_level', 'reorder_level', 'reorder_point', 'min_qty'],
    barcode: ['barcode', 'ean', 'upc'],
    supplier: ['supplier', 'vendor'],
    isActive: ['isactive', 'is_active', 'active', 'enabled'],
  };

  for (const [target, candidates] of Object.entries(suggestions)) {
    const matched = pick(candidates);
    if (matched) mapping[target] = matched;
  }

  return mapping;
};

const InventoryUploadPage: React.FC = () => {
  const [step, setStep] = useState<Step>('setup');
  const [fileType, setFileType] = useState<FileType>('csv');
  const [sourceSystem, setSourceSystem] = useState('inventory_upload');
  const [connectorId, setConnectorId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<FileUploadResponse | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<any | null>(null);

  const requiredTargets = useMemo(() => TARGET_FIELDS.filter((f) => f.required).map((f) => f.name), []);

  const canImport = useMemo(() => {
    if (!connectorId) return false;
    if (!uploadPreview) return false;
    return requiredTargets.every((t) => mapping[t]);
  }, [connectorId, uploadPreview, requiredTargets, mapping]);

  const createConnectorMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: 'Inventory Upload',
        entityType: 'product',
        sourceSystem,
        fileType,
      };
      const res = await api.post('/api/integration/connectors/file', payload);
      return res.data?.data as { id: string };
    },
    onSuccess: (data) => {
      setConnectorId(data.id);
      toast.success('Upload session created');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error || 'Failed to create upload session');
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async () => {
      if (!connectorId || !selectedFile) throw new Error('Missing connector or file');
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post(`/api/integration/connectors/${connectorId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data?.data as FileUploadResponse;
    },
    onSuccess: (data) => {
      setUploadPreview(data);
      setMapping(suggestMapping(data.sourceFields || []));
      setStep('uploaded');
      toast.success('File uploaded');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error || 'Failed to upload file');
    },
  });

  const saveMappingMutation = useMutation({
    mutationFn: async () => {
      if (!connectorId) throw new Error('Missing connector');
      const payload = {
        fieldMappings: Object.entries(mapping)
          .filter(([, source]) => !!source)
          .map(([target, source]) => ({
            source,
            target,
            required: requiredTargets.includes(target),
          })),
      };
      const res = await api.put(`/api/integration/connectors/${connectorId}/mapping`, payload);
      return res.data;
    },
    onSuccess: () => {
      setStep('mapped');
      toast.success('Mapping saved');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error || 'Failed to save mapping');
    },
  });

  const runImportMutation = useMutation({
    mutationFn: async () => {
      if (!connectorId) throw new Error('Missing connector');
      const res = await api.post(`/api/integration/connectors/${connectorId}/sync`);
      return res.data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      setStep('imported');
      toast.success('Import completed');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error || 'Import failed');
    },
  });

  const reset = () => {
    setStep('setup');
    setConnectorId(null);
    setSelectedFile(null);
    setUploadPreview(null);
    setMapping({});
    setImportResult(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Upload</h1>
          <p className="text-sm text-gray-600">
            Upload products using CSV, Excel, or JSON and map fields before importing.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={reset}>
            Reset
          </Button>
          <Button
            onClick={() => createConnectorMutation.mutate()}
            disabled={createConnectorMutation.isPending || !!connectorId}
          >
            Create Upload Session
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File type</label>
            <select
              className="w-full p-2 border rounded"
              value={fileType}
              onChange={(e) => setFileType(e.target.value as FileType)}
              disabled={!!connectorId}
            >
              <option value="csv">CSV</option>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="json">JSON</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source system</label>
            <Input
              value={sourceSystem}
              onChange={(e) => setSourceSystem((e.target as HTMLInputElement).value)}
              placeholder="inventory_upload"
              disabled={!!connectorId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
            <div className="w-full p-2 border rounded bg-gray-50 text-sm text-gray-700">
              {connectorId ? connectorId : 'Not created'}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <input
            type="file"
            accept={fileType === 'csv' ? '.csv' : fileType === 'xlsx' ? '.xlsx' : '.json'}
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            className="w-full p-2 border rounded"
            disabled={!connectorId}
          />
          <Button
            onClick={() => uploadFileMutation.mutate()}
            disabled={!connectorId || !selectedFile || uploadFileMutation.isPending}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Tip: Required fields are <span className="font-medium">name</span>, <span className="font-medium">sku</span>, and <span className="font-medium">price</span>.
        </div>
      </div>

      {uploadPreview && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              <p className="text-sm text-gray-600">
                File: {uploadPreview.fileName} ({uploadPreview.totalRows} rows)
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  {(uploadPreview.sourceFields || []).slice(0, 8).map((f) => (
                    <th key={f} className="text-left p-2 font-medium text-gray-700">
                      {f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(uploadPreview.preview || []).slice(0, 5).map((row, idx) => (
                  <tr key={idx} className="border-b">
                    {(uploadPreview.sourceFields || []).slice(0, 8).map((f) => (
                      <td key={f} className="p-2 text-gray-700">
                        {row?.data?.[f] !== undefined && row?.data?.[f] !== null ? String(row.data[f]) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900">Field mapping</h2>
            <p className="text-sm text-gray-600">
              Drag source fields onto target fields. The system will enforce required mappings during import.
            </p>
          </div>

          <FieldMappingEditor
            sourceFields={uploadPreview.sourceFields || []}
            targetFields={TARGET_FIELDS}
            initialMapping={mapping}
            onChange={setMapping}
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                if (uploadPreview?.sourceFields) setMapping(suggestMapping(uploadPreview.sourceFields));
              }}
              disabled={!uploadPreview}
            >
              Auto-map
            </Button>
            <Button
              onClick={() => saveMappingMutation.mutate()}
              disabled={!connectorId || saveMappingMutation.isPending}
            >
              Save Mapping
            </Button>
            <Button
              onClick={() => runImportMutation.mutate()}
              disabled={!canImport || runImportMutation.isPending || (step !== 'mapped' && step !== 'uploaded')}
            >
              Import
            </Button>
          </div>

          <div className="text-xs text-gray-500">
            {step === 'uploaded' && 'Next: Save mapping, then import.'}
            {step === 'mapped' && 'Ready: Click Import.'}
            {step === 'imported' && 'Done: Import completed.'}
          </div>
        </div>
      )}

      {importResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Import results</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded bg-gray-50 border">
              <div className="text-gray-500">Processed</div>
              <div className="text-gray-900 font-semibold">
                {importResult?.data?.entitiesProcessed ?? importResult?.data?.entitiesProcessed ?? 0}
              </div>
            </div>
            <div className="p-3 rounded bg-gray-50 border">
              <div className="text-gray-500">Created</div>
              <div className="text-gray-900 font-semibold">
                {importResult?.data?.entitiesCreated ?? 0}
              </div>
            </div>
            <div className="p-3 rounded bg-gray-50 border">
              <div className="text-gray-500">Updated</div>
              <div className="text-gray-900 font-semibold">
                {importResult?.data?.entitiesUpdated ?? 0}
              </div>
            </div>
          </div>

          {Array.isArray(importResult?.data?.errors) && importResult.data.errors.length > 0 && (
            <div className="border rounded p-3 bg-red-50">
              <div className="text-sm font-semibold text-red-700 mb-2">Errors</div>
              <div className="text-xs text-red-700 whitespace-pre-wrap">
                {JSON.stringify(importResult.data.errors, null, 2)}
              </div>
            </div>
          )}

          {Array.isArray(importResult?.data?.warnings) && importResult.data.warnings.length > 0 && (
            <div className="border rounded p-3 bg-yellow-50">
              <div className="text-sm font-semibold text-yellow-800 mb-2">Warnings</div>
              <div className="text-xs text-yellow-800 whitespace-pre-wrap">
                {JSON.stringify(importResult.data.warnings, null, 2)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryUploadPage;
